/**
 * Hook to automatically sync TanStack Query to Reatom atom
 * Eliminates the need for manual useEffect syncing
 */

import type { Atom } from "@reatom/core"
import { useWrap } from "@reatom/react"
import type { UseQueryResult } from "@tanstack/react-query"
import { useEffect } from "react"
import type { QueryAtomState } from "./reatom-query"

/**
 * Automatically syncs a TanStack Query result to a Reatom atom
 * Use this to eliminate boilerplate useEffect code
 *
 * @example
 * ```ts
 * const BlockchainsDataProvider = () => {
 *   const bffApiClient = useMolecule(BffApiClientMolecule)
 *   const { blockchains } = useMolecule(BlockchainsMoleculeReatom)
 *
 *   const query = useQuery(getBlockchainsQueryParams(bffApiClient))
 *   useSyncQueryToAtom(blockchains, query)
 *
 *   return null
 * }
 * ```
 */
export function useSyncQueryToAtom<TData, TError = Error>(
  queryAtom: Atom<QueryAtomState<TData, TError>>,
  queryResult: UseQueryResult<TData, TError>,
) {
  const syncQuery = useWrap(() => {
    const isPending = queryResult.isPending
    const status = queryResult.status

    queryAtom.set({
      data: queryResult.data,
      isLoading: isPending,
      isPending,
      error: queryResult.error ?? undefined,
      status,
      isError: status === "error",
      isSuccess: status === "success",
    })
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    syncQuery()
  }, [
    queryResult.data,
    queryResult.isPending,
    queryResult.error,
    queryResult.status,
    syncQuery,
  ])
}
